import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import MonacoFileEditor from './MonacoFileEditor';
import {syncCollectionToCrud} from '../utils/crudServiceSync';

/**
 * CollectionEditor Component
 * Provides a JSON editor for CRUD collection definitions
 */
const CollectionEditor = ({
  collection,
  projectId,
  onSave,
  readOnly = false
}) => {
  const [jsonContent, setJsonContent] = useState('');
  const [error, setError] = useState(null);

  // Convert collection object to formatted JSON when collection changes
  useEffect(() => {
    if (collection?.data) {
      try {
        const formatted = JSON.stringify(collection.data, null, 2);
        setJsonContent(formatted);
        setError(null);
      } catch (err) {
        setError(`Failed to format collection: ${err.message}`);
      }
    }
  }, [collection]);

  // Handle save from Monaco editor
  const handleSave = (newContent) => {
    try {
      // Handle both string and object inputs
      let parsed;
      
      // Check if newContent is a file object with content property
      if (newContent && typeof newContent === 'object' && newContent.content) {
        // Extract content from file object
        const contentString = newContent.content;
        parsed = JSON.parse(contentString);
      } else if (typeof newContent === 'string') {
        // Parse JSON string to validate
        parsed = JSON.parse(newContent);
      } else if (typeof newContent === 'object' && newContent !== null && newContent.fields) {
        // Already a parsed collection object, use directly
        parsed = newContent;
      } else {
        throw new Error('Invalid content type');
      }
      
      // Debug log to see what we're working with
      console.log('📝 Saving collection, parsed data:', parsed); /* eslint-disable-line no-console */
      
      // Ensure required fields
      if (!parsed.name) {
        throw new Error('Collection must have a "name" field');
      }
      
      // More flexible fields validation - check if it exists and has items
      if (!parsed.fields) {
        throw new Error('Collection must have a "fields" property');
      }
      
      if (!Array.isArray(parsed.fields)) {
        // Try to handle if fields is an object that should be an array
        if (typeof parsed.fields === 'object' && parsed.fields !== null) {
          console.warn('⚠️ fields is an object, attempting to convert to array'); /* eslint-disable-line no-console */
          parsed.fields = Object.values(parsed.fields);
        } else {
          throw new Error(`Collection "fields" must be an array, got ${typeof parsed.fields}`);
        }
      }
      
      if (parsed.fields.length === 0) {
        throw new Error('Collection must have at least one field');
      }

      // Save to localStorage
      if (projectId) {
        const collectionsKey = `collections_${projectId}`;
        const existingCollections = JSON.parse(localStorage.getItem(collectionsKey) || '{}');
        
        existingCollections[collection.name] = {
          ...parsed,
          id: collection.name, // Keep original ID
          isNew: false, // Mark as saved
          deleted: false
        };
        
        localStorage.setItem(collectionsKey, JSON.stringify(existingCollections));
        
        // eslint-disable-next-line no-console
        console.log('💾 Collection saved:', collection.name);
        
        // Sync to crud-service-universal
        syncCollectionToCrud(projectId, collection.name, parsed).catch(err => {
          console.warn(`⚠️ Failed to sync collection ${collection.name} to crud-service-universal:`, err); /* eslint-disable-line no-console */
        });
      }

      // Notify parent
      if (onSave) {
        onSave({
          ...parsed,
          id: collection.name
        });
      }

      setError(null);
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      // eslint-disable-next-line no-console
      console.error('Failed to save collection:', err);
    }
  };

  // Create a file-like object for MonacoFileEditor
  const fileObject = {
    path: `${collection?.name || 'collection'}.json`,
    name: `${collection?.name || 'collection'}.json`,
    content: jsonContent,
    language: 'json'
  };

  return (
    <div className="collection-editor" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
      {error && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#f44336',
            color: 'white',
            marginBottom: '10px'
          }}
        >
          {error}
        </div>
      )}
      <MonacoFileEditor
        file={fileObject}
        onSave={handleSave}
        projectId={projectId}
        readOnly={readOnly}
        serviceName="crud-collections"
      />
    </div>
  );
};

CollectionEditor.propTypes = {
  collection: PropTypes.shape({
    name: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired
  }),
  onSave: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool
};

CollectionEditor.defaultProps = {
  collection: null,
  onSave: null,
  projectId: null,
  readOnly: false
};

export default CollectionEditor;
